import { useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import TagIcon from "@mui/icons-material/Tag";
import MoreIcon from "@mui/icons-material/More";
import UploadIcon from "@mui/icons-material/Upload";
import ViewListIcon from "@mui/icons-material/ViewList";

import conf from "config-dlm";
import Spinner from "components/utils/Spinner";
import Refresh from "components/utils/Refresh";
import EmptyTableRow from "components/rows/Empty";
import SectionHeader from "components/utils/SectionHeader";
import LinearIndeterminate from "components/utils/LinearLoader";
import {
  FwupdateSchema,
  FwupdatesSchema,
  usePavmFwupdateUpload,
  usePavmFwupdates,
} from "hooks/pavm/fwupdate";

type ReleasesTableProps = {
  host: string;
  uploaded: FwupdateSchema["uploaded"] | undefined;
};
const ReleasesTable = ({ host, uploaded }: ReleasesTableProps) => {
  const uploadedName = uploaded?.name;
  const uploadedStatus = uploaded?.status;

  // tag used to identify release being uploaded (used to show loading spinner)
  const [uploadTag, setUploadTag] = useState("");

  const upload = usePavmFwupdateUpload(host);
  const fwupdates = usePavmFwupdates();

  const handleUpload = (release: FwupdatesSchema) => {
    const { key, size, eTag, lastModified, checksumAlgorithm } = release;

    setUploadTag(eTag);
    upload.mutate({ key, size, eTag, lastModified, checksumAlgorithm });
  };

  return (
    <Box>
      <SectionHeader
        title="Releases"
        icon={<ViewListIcon />}
        buttons={[
          <Refresh
            onClick={fwupdates.refetch}
            isFetching={fwupdates.isLoading}
          />,
        ]}
      />

      <TableContainer sx={{ boxShadow: 2, mb: 2 }}>
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow>
              <TableCell />

              <TableCell align="right">
                <b>Size (MB)</b>
              </TableCell>

              <TableCell align="right">
                <b>Tag</b>
              </TableCell>

              <TableCell align="right">
                <b>Algo</b>
              </TableCell>

              <TableCell align="right">
                <b>Last modified</b>
              </TableCell>

              <TableCell align="right">
                <UploadIcon fontSize="small" />
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {fwupdates.isLoading ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <LinearIndeterminate />
                </TableCell>
              </TableRow>
            ) : fwupdates.isError || fwupdates.data?.length === 0 ? (
              <EmptyTableRow colSpan={8} type="releases" />
            ) : (
              fwupdates.data?.map((release) => {
                const { key, size, eTag, lastModified, checksumAlgorithm } =
                  release;

                return (
                  <TableRow
                    key={`${key}-${lastModified}`}
                    sx={conf.LAST_CHILD_NO_BORDER}
                  >
                    <TableCell>
                      <b>{key?.replace("releases/", "")}</b>
                    </TableCell>

                    <TableCell align="right">
                      {Math.round(size / 10000) / 100}
                    </TableCell>

                    <TableCell align="right">
                      <Tooltip title={eTag}>
                        <TagIcon fontSize="small" />
                      </Tooltip>
                    </TableCell>

                    <TableCell align="right">
                      <Tooltip title={checksumAlgorithm}>
                        <MoreIcon fontSize="small" />
                      </Tooltip>
                    </TableCell>

                    <TableCell align="right">
                      {new Date(lastModified).toUTCString()}
                    </TableCell>

                    <TableCell align="right">
                      {upload.isLoading && eTag === uploadTag ? (
                        <Button variant="contained" sx={{ p: 1, width: "80%" }}>
                          <Spinner />
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          disabled={
                            upload.isLoading ||
                            uploadedName === key ||
                            uploadedStatus === "REQUESTED" ||
                            uploadedStatus === "SCHEDULED" ||
                            uploadedStatus === "INSTALLING"
                          }
                          onClick={() => handleUpload(release)}
                        >
                          Upload
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ReleasesTable;
